import { AbilitySwitch } from '@efdigital/ll-addon-auth';
import type { Cohort, UserGroup } from '@efdigital/ll-addon-data';
import { EditContext } from '@efdigital/ll-admin-v2/components/edit-context.gts';
import { EditUserGroupUsers } from '@efdigital/ll-admin-v2/components/programs/edit-user-group-users.gts';
import { setAsyncBelongsTo } from '@efdigital/ll-admin-v2/data/async-setter';
import { deepSave } from '@efdigital/ll-admin-v2/data/deep-save';
import type { ModelFrom } from '@efdigital/ll-admin-v2/lib/type-utils';
import type ProgramsProgramCohortParticipantsRoute from '@efdigital/ll-admin-v2/routes/programs/program/cohort/participants';
import { fn } from '@ember/helper';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import type { Store } from '@warp-drive/core';
import { pageTitle } from 'ember-page-title';

interface ParticipantsSignature {
  Args: {
    model: ModelFrom<ProgramsProgramCohortParticipantsRoute>;
    controller: unknown;
  };
}

class ProgramsProgramCohortParticipantsTemplate extends Component<ParticipantsSignature> {
  @service declare store: Store;

  saveGroup = (cohort: Cohort, group: UserGroup) => {
    setAsyncBelongsTo(cohort, 'group', group);
    return deepSave(cohort, this.store);
  };

  saveAdminGroup = (cohort: Cohort, admingroup: UserGroup) => {
    setAsyncBelongsTo(cohort, 'admingroup', admingroup);
    return deepSave(cohort, this.store);
  };

  <template>
    <div class="container py-5">
      {{pageTitle "Index"}}
      <AbilitySwitch @ability="participants">
        <:editor>
          <EditContext as |context|>
            <div class="row g-5">
              <div class="col-xl-6">
                <EditUserGroupUsers
                  @item={{@model.group}}
                  @save={{fn this.saveGroup @model.cohort}}
                  @context={{context}}
                  @editLabel="Edit Participants"
                  @legend="Participants"
                  @helpText="These are the learners enrolled in this cohort. Do not include faculty, facilitators, program administrators or other staff."
                />
              </div>
              <div class="col-xl-6">
                <EditUserGroupUsers
                  @item={{@model.admingroup}}
                  @save={{fn this.saveAdminGroup @model.cohort}}
                  @context={{context}}
                  @editLabel="Edit Staff"
                  @legend="Staff"
                  @helpText="These are the staff members who will be responsible for delivering the program. They will have access to the program and can manage the cohort, and will not be included in any reports. Include faculty, facilitators, program administrators and any other relevant people."
                />
              </div>
            </div>
          </EditContext>
        </:editor>
        <:viewer>
          <div class="alert alert-info" role="alert">
            <h4 class="alert-heading">Participants (View Only)</h4>
            <p>You have view-only access to participants.</p>
            <p class="mb-0">To edit participants, you need the "Edit individual
              users" permission.</p>
          </div>
        </:viewer>
        <:none>
          <div class="alert alert-warning" role="alert">
            <h4 class="alert-heading">Access Denied</h4>
            <p>You do not have permission to view participants.</p>
            <p class="mb-0">Please contact your administrator if you believe you
              should have access.</p>
          </div>
        </:none>
      </AbilitySwitch>
    </div>
  </template>
}

export default ProgramsProgramCohortParticipantsTemplate;
